<script lang="ts">
  import { Save, User } from '@lucide/svelte';

  interface Props {
    currentUser: any;
    addToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
  }

  let { currentUser, addToast }: Props = $props();

  // Local shipping defaults state
  let contactNo = $state('');
  let addressLine = $state('');
  let landmark = $state('');
  let district = $state('');
  let stateVal = $state('');
  let pincode = $state('');

  // Load defaults from localStorage on mount
  $effect(() => {
    if (typeof window !== 'undefined') {
      contactNo = localStorage.getItem('default_contact_no') || '';
      addressLine = localStorage.getItem('default_address_line') || '';
      landmark = localStorage.getItem('default_landmark') || '';
      district = localStorage.getItem('default_district') || '';
      stateVal = localStorage.getItem('default_state') || '';
      pincode = localStorage.getItem('default_pincode') || '';
    }
  });

  function handleSave(e: SubmitEvent) {
    e.preventDefault();
    if (typeof window !== 'undefined') {
      localStorage.setItem('default_contact_no', contactNo.trim());
      localStorage.setItem('default_address_line', addressLine.trim());
      localStorage.setItem('default_landmark', landmark.trim());
      localStorage.setItem('default_district', district.trim());
      localStorage.setItem('default_state', stateVal.trim());
      localStorage.setItem('default_pincode', pincode.trim());
      addToast('Profile shipping defaults successfully updated.', 'success');
    }
  }
</script>

<div class="space-y-6 max-w-2xl">
  <div class="space-y-1">
    <h2 class="text-3xl font-extrabold tracking-tight text-base-content font-heading">Account Settings</h2>
    <p class="text-sm text-base-content/70">Update your default shipping address and personal contact details</p>
  </div>

  <form onsubmit={handleSave} class="space-y-6">
    <!-- User Profile Card -->
    <div class="bg-base-100 border border-base-300 rounded-3xl p-6 shadow-sm space-y-4">
      <h3 class="text-sm font-bold uppercase tracking-wider text-base-content/75 flex items-center gap-2">
        <User class="w-4 h-4 text-accent" /> Profile Information
      </h3>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="form-control">
          <label class="label pb-1" for="profile-name">
            <span class="label-text text-xs text-base-content/50 uppercase font-bold">Full Name</span>
          </label>
          <input
            type="text"
            id="profile-name"
            disabled
            value={currentUser?.name || ''}
            class="input input-bordered bg-base-200/50 text-base-content/60 cursor-not-allowed rounded-xl"
          />
        </div>
        <div class="form-control">
          <label class="label pb-1" for="profile-email">
            <span class="label-text text-xs text-base-content/50 uppercase font-bold">Email Address</span>
          </label>
          <input
            type="email"
            id="profile-email"
            disabled
            value={currentUser?.email || ''}
            class="input input-bordered bg-base-200/50 text-base-content/60 cursor-not-allowed rounded-xl"
          />
        </div>
      </div>
    </div>

    <!-- Shipping Defaults Card -->
    <div class="bg-base-100 border border-base-300 rounded-3xl p-6 shadow-sm space-y-4">
      <h3 class="text-sm font-bold uppercase tracking-wider text-base-content/75">Default Shipping Address</h3>
      
      <div class="form-control">
        <label class="label pb-1" for="contactNo">
          <span class="label-text text-xs text-base-content/50 uppercase font-bold">Contact Phone Number</span>
        </label>
        <input
          type="text"
          id="contactNo"
          bind:value={contactNo}
          placeholder="+91 98765 43210"
          class="input input-bordered w-full rounded-xl"
        />
      </div>

      <div class="form-control">
        <label class="label pb-1" for="addressLine">
          <span class="label-text text-xs text-base-content/50 uppercase font-bold">Street Address / Line</span>
        </label>
        <textarea
          id="addressLine"
          bind:value={addressLine}
          rows="2"
          placeholder="Flat, House no., Building, Street name"
          class="textarea textarea-bordered w-full rounded-xl resize-none leading-relaxed"
        ></textarea>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="form-control">
          <label class="label pb-1" for="landmark">
            <span class="label-text text-xs text-base-content/50 uppercase font-bold">Landmark (Optional)</span>
          </label>
          <input
            type="text"
            id="landmark"
            bind:value={landmark}
            placeholder="Nearby landmark"
            class="input input-bordered w-full rounded-xl"
          />
        </div>
        <div class="form-control">
          <label class="label pb-1" for="district">
            <span class="label-text text-xs text-base-content/50 uppercase font-bold">District / City</span>
          </label>
          <input
            type="text"
            id="district"
            bind:value={district}
            placeholder="District name"
            class="input input-bordered w-full rounded-xl"
          />
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="form-control">
          <label class="label pb-1" for="state">
            <span class="label-text text-xs text-base-content/50 uppercase font-bold">State</span>
          </label>
          <input
            type="text"
            id="state"
            bind:value={stateVal}
            placeholder="State"
            class="input input-bordered w-full rounded-xl"
          />
        </div>
        <div class="form-control">
          <label class="label pb-1" for="pincode">
            <span class="label-text text-xs text-base-content/50 uppercase font-bold">Pincode</span>
          </label>
          <input
            type="text"
            id="pincode"
            bind:value={pincode}
            placeholder="6-digit ZIP code"
            pattern="^[1-9][0-9]{5}$"
            class="input input-bordered w-full rounded-xl"
          />
        </div>
      </div>
    </div>

    <!-- Action Bar -->
    <div class="flex justify-end pt-2">
      <button
        type="submit"
        class="btn btn-accent rounded-xl font-bold uppercase tracking-wider text-xs flex items-center gap-2 cursor-pointer text-white h-10 px-6"
      >
        <Save class="w-4 h-4" /> Save Default Shipping
      </button>
    </div>
  </form>
</div>
